/**
 * Shared validation boundary for the notification activity RPC envelopes.
 *
 * `xan_get_recent_activity` and `xan_mark_notifications_read` both return
 * `jsonb`, which the SDK surfaces as an untyped value. Casting that value
 * asserts a shape the caller never checked, so a backend contract change
 * reaches the UI as an undefined field rather than a handled failure. Parsing
 * through this module turns that into one catchable error type.
 *
 * Schemas passed here MUST NOT be strict: the backend is expected to add
 * envelope fields (`last_read_id`, `unread_count`) and an unrecognized key is
 * dropped rather than treated as a failure.
 */
import type { z } from "zod";

export class NotificationEnvelopeValidationError extends Error {
  constructor(envelope: string, cause: z.ZodError) {
    super(`Invalid ${envelope} response`, { cause });
    this.name = "NotificationEnvelopeValidationError";
  }
}

export function parseNotificationEnvelope<TSchema extends z.ZodType>(
  envelope: string,
  schema: TSchema,
  data: unknown,
): z.output<TSchema> {
  const result = schema.safeParse(data);

  if (!result.success) {
    throw new NotificationEnvelopeValidationError(envelope, result.error);
  }

  return result.data;
}
